import React, { useEffect, useState } from "react";
import axios from "axios";
import { Archive, RefreshCw, Shield, AlertTriangle, CheckCircle, XCircle, Undo2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function Section({ title, data, kind, refresh }) {
  const rows = data?.rows || [];
  const [archiving, setArchiving] = useState(false);

  const archiveSelected = async () => {
    const safe = rows.filter(x => x.safe_to_archive).map(x => x.job_id || x.contractor_id);
    if (safe.length === 0) {
      alert("No safe records to archive");
      return;
    }
    
    setArchiving(true);
    try {
      const key = kind === "jobs" ? "job_ids" : "contractor_ids";
      await axios.post(`${API}/api/admin/test-data/${kind}/archive`, { [key]: safe });
      refresh();
    } catch (err) {
      alert("Archive failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`section-${kind}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-semibold text-slate-900">{title}</div>
          <div className="mt-1 text-sm text-slate-500">
            Found: {data?.count || 0} · 
            <span className="text-teal-600 font-medium"> Safe: {data?.safe_count || 0}</span> · 
            <span className="text-amber-600 font-medium"> Manual Review: {data?.manual_review_count || 0}</span>
          </div>
        </div>
        <button 
          onClick={archiveSelected} 
          disabled={archiving || (data?.safe_count || 0) === 0}
          className="rounded-full bg-teal-600 px-5 py-3 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300 transition flex items-center gap-2"
          data-testid={`archive-${kind}-btn`}
        >
          <Archive className="w-4 h-4" />
          {archiving ? "Archiving..." : `Archive Safe (${data?.safe_count || 0})`}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="mt-5 text-center text-slate-500 py-8">
          No test/legacy data candidates found
        </div>
      ) : (
        <div className="mt-5 space-y-3 max-h-[500px] overflow-y-auto">
          {rows.map((row, idx) => (
            <div 
              key={idx} 
              className={`rounded-2xl border p-4 ${row.safe_to_archive ? 'border-teal-200 bg-teal-50/50' : 'border-amber-200 bg-amber-50/50'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">
                    {row.job_reference || row.company_name || row.contractor_id || row.job_id}
                  </div>
                  <div className="mt-1 text-sm text-slate-500 truncate">
                    {row.description || row.email || row.mobile || row.park_name || "-"}
                  </div>
                </div>
                <div className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                  row.safe_to_archive 
                    ? "bg-teal-100 text-teal-700" 
                    : "bg-amber-100 text-amber-700"
                }`}>
                  {row.safe_to_archive ? "SAFE" : "REVIEW"}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(row.flags || []).map((flag, i) => (
                  <span 
                    key={i} 
                    className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                  >
                    {flag}
                  </span>
                ))}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Linked: {Object.entries(row.linked_counts || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TestDataPurgePage() {
  const [jobs, setJobs] = useState(null);
  const [contractors, setContractors] = useState(null);
  const [liveAudit, setLiveAudit] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [archivingAll, setArchivingAll] = useState(false);
  const [runningAudit, setRunningAudit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [j, c, a, s] = await Promise.all([
        axios.get(`${API}/api/admin/test-data/jobs/preview?limit=500`),
        axios.get(`${API}/api/admin/test-data/contractors/preview?limit=500`),
        axios.get(`${API}/api/admin/test-data/lockdown/latest-live`),
        axios.get(`${API}/api/admin/test-data/stats`),
      ]);
      setJobs(j.data);
      setContractors(c.data);
      setLiveAudit(a.data);
      setStats(s.data);
    } catch (err) {
      console.error("Failed to load:", err);
    } finally {
      setLoading(false);
    }
  };

  const archiveAllSafe = async () => {
    setArchivingAll(true);
    try {
      await axios.post(`${API}/api/admin/test-data/archive-all-safe`);
      await runLiveAudit();
      await load();
    } catch (err) {
      alert("Archive failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setArchivingAll(false);
    }
  };

  const runLiveAudit = async () => {
    setRunningAudit(true);
    try {
      const res = await axios.post(`${API}/api/admin/test-data/lockdown/run-live`);
      setLiveAudit(res.data.audit);
    } catch (err) {
      alert("Audit failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setRunningAudit(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
      </div>
    );
  }

  const trafficColor = {
    GREEN: "from-emerald-700 to-emerald-600",
    AMBER: "from-amber-600 to-amber-500",
    RED: "from-red-700 to-red-600",
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="test-data-purge-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="flex items-center gap-4">
            <Archive className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-semibold">Test Data Purge + Live Audit</h1>
              <p className="mt-1 text-sm text-teal-100">
                Archive legacy/test data and view true live readiness
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="mx-auto max-w-7xl px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <button 
              onClick={archiveAllSafe} 
              disabled={archivingAll}
              className="rounded-full bg-teal-600 px-5 py-3 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300 transition flex items-center gap-2"
              data-testid="archive-all-safe-btn"
            >
              <Archive className={`w-4 h-4 ${archivingAll ? "animate-pulse" : ""}`} />
              {archivingAll ? "Archiving..." : "Archive All Safe"}
            </button>
            <button 
              onClick={runLiveAudit} 
              disabled={runningAudit}
              className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition flex items-center gap-2"
              data-testid="run-live-audit-btn"
            >
              <Shield className={`w-4 h-4 ${runningAudit ? "animate-pulse" : ""}`} />
              {runningAudit ? "Running..." : "Run Live Audit"}
            </button>
            <button 
              onClick={load} 
              className="rounded-full bg-white border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="mx-auto max-w-7xl px-6 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center">
              <div className="text-2xl font-bold text-slate-800">{stats.jobs?.total || 0}</div>
              <div className="text-xs text-slate-500">Total Jobs</div>
            </div>
            <div className="rounded-2xl border border-teal-200 bg-teal-50 p-4 text-center">
              <div className="text-2xl font-bold text-teal-700">{stats.jobs?.live || 0}</div>
              <div className="text-xs text-teal-600">Live Jobs</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center">
              <div className="text-2xl font-bold text-slate-800">{stats.contractors?.total || 0}</div>
              <div className="text-xs text-slate-500">Total Contractors</div>
            </div>
            <div className="rounded-2xl border border-teal-200 bg-teal-50 p-4 text-center">
              <div className="text-2xl font-bold text-teal-700">{stats.contractors?.live || 0}</div>
              <div className="text-xs text-teal-600">Live Contractors</div>
            </div>
          </div>
        </div>
      )}

      {/* Live Audit Status */}
      {liveAudit && liveAudit.status !== "NO_AUDIT" && (
        <div className="mx-auto max-w-7xl px-6 pb-4">
          <div 
            className={`rounded-3xl bg-gradient-to-r ${trafficColor[liveAudit.traffic] || trafficColor.RED} p-5 text-white`}
            data-testid="live-audit-status"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {liveAudit.traffic === "GREEN" ? (
                  <CheckCircle className="w-8 h-8" />
                ) : liveAudit.traffic === "AMBER" ? (
                  <AlertTriangle className="w-8 h-8" />
                ) : (
                  <XCircle className="w-8 h-8" />
                )}
                <div>
                  <div className="text-2xl font-semibold">
                    Live Readiness: {liveAudit.status}
                  </div>
                  <div className="text-sm opacity-90">
                    Traffic: {liveAudit.traffic} · Score: {liveAudit.score}%
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">{liveAudit.score}%</div>
                <div className="text-sm opacity-90">
                  {liveAudit.traffic === "GREEN" ? "GO LIVE READY" : "NOT READY"}
                </div>
              </div>
            </div>

            {/* Checks breakdown */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
              {(liveAudit.checks || []).map((check, i) => (
                <div 
                  key={i} 
                  className={`rounded-xl p-2 text-sm ${check.passed ? 'bg-white/20' : 'bg-white/10 border border-white/30'}`}
                >
                  <div className="flex items-center gap-1">
                    {check.passed ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    <span className="truncate">{check.name}</span>
                  </div>
                  {!check.passed && check.count > 0 && (
                    <div className="text-xs opacity-75 mt-1">{check.count} issues</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {liveAudit && liveAudit.status === "NO_AUDIT" && (
        <div className="mx-auto max-w-7xl px-6 pb-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 text-center">
            <Shield className="w-12 h-12 text-slate-400 mx-auto" />
            <div className="mt-2 text-lg font-medium text-slate-800">No Live Audit Yet</div>
            <div className="text-sm text-slate-500">Click "Run Live Audit" to check live readiness</div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 pb-8 space-y-6">
        <Section 
          title="Test / Legacy Jobs" 
          data={jobs} 
          kind="jobs" 
          refresh={load} 
        />
        <Section 
          title="Test / Legacy Contractors" 
          data={contractors} 
          kind="contractors" 
          refresh={load} 
        />
      </div>
    </div>
  );
}
